import { AC_CARD_STATUSES, type MeResponse } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import "../accreditation/accreditation.css";
import { accreditationSummaryOptions } from "../../lib/api/accreditations";
import { errorMessage } from "../../lib/api/messages";
import type { SectorNumber } from "../../lib/dashboard/sectors";
import { CardsStrip } from "./cards-strip";
import { CategoriesCard, NeedsAttention, OrganisationsCard, QuickActions, StatusSummaryCard } from "./cards";
import "./dashboard.css";
import { DashboardFilters } from "./filters";
import { FooterBand } from "./footer-band";
import { DashboardHero } from "./hero";
import { type DashboardSearch, toSummaryFilter } from "./model";
import { KpiTiles } from "./tiles";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

/** URL = the dashboard's filters (platform rule), in the summary endpoint's own keys. Lenient: a bad value = not set. */
export const accreditationDashboardSearch = z.object({
	games_id: lenient(z.string().uuid()),
	responsible_organization_id: lenient(z.string().uuid()),
	main_function_id: lenient(z.string().uuid()),
	sub_function_id: lenient(z.string().uuid()),
	status: lenient(z.enum(AC_CARD_STATUSES)),
});

/**
 * The designer's dashboard: the accreditation numbers. Sector 2's Dashboard
 * (`/dashboard/accreditation`), and "/" for the roles without sectors. Every
 * number on it comes from ONE `GET /accreditations/summary` per filter change
 * (Rule 0), scoped to the viewer in SQL — a scoped login just sees its own
 * numbers; the cards strip is one `GET /cards/summary` for the same games.
 */
export function AccreditationDashboard({
	session,
	search,
	onChange,
	sector,
}: {
	session: MeResponse;
	search: DashboardSearch;
	onChange: (patch: Partial<DashboardSearch>) => void;
	sector?: SectorNumber;
}) {
	const { t } = useTranslation("dashboard");
	const summary = useQuery(accreditationSummaryOptions(toSummaryFilter(search)));
	const data = summary.data;

	return (
		<div className="flex flex-col">
			<DashboardHero session={session} sector={sector} />
			<DashboardFilters session={session} value={search} onChange={onChange} />

			{summary.isError && !data ? (
				<div role="alert" className="mt-[1rem] flex flex-wrap items-center gap-[0.75rem] rounded-[0.625rem] border border-gms-bad/30 bg-gms-bad-soft px-[1rem] py-[0.875rem]">
					<div className="min-w-0 flex-1">
						<p className="text-[0.875rem] font-semibold text-gms-bad">{t("error.title")}</p>
						<p className="text-[0.8125rem] text-gms-ink-2">{errorMessage(summary.error)}</p>
					</div>
					<button type="button" className="btn btn-sm btn-line" onClick={() => summary.refetch()}>
						{t("error.retry")}
					</button>
				</div>
			) : (
				<>
					<KpiTiles summary={data} search={search} />
					<div className="db-sum">
						<StatusSummaryCard summary={data} search={search} />
						<OrganisationsCard summary={data} search={search} />
					</div>
					{sector !== undefined && <CardsStrip gamesId={search.games_id} orgId={search.responsible_organization_id} />}
					<div className="db-dl">
						<div className="db-dl__a">
							<NeedsAttention summary={data} search={search} session={session} />
							<QuickActions session={session} />
						</div>
						<CategoriesCard summary={data} search={search} />
					</div>
				</>
			)}
			<FooterBand />
		</div>
	);
}
